(() => {
  const roots = document.querySelectorAll('[data-diggn-review-carousel]');

  const decodeHtml = (value) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value || '');
    return textarea.value;
  };

  const cleanDisplayText = (value) =>
    decodeHtml(value)
      .replace(/[\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\s+([،,.!?؟])/g, '$1')
      .trim();

  const formatName = (value) => {
    const name = cleanDisplayText(value);
    const parts = name.split(' ').filter(Boolean);

    if (parts.length < 2) return name;

    const first = parts[0];
    const last = parts[parts.length - 1].replace(/[^\p{L}\p{N}]/gu, '');
    const initial = Array.from(last)[0];

    return initial ? `${first} ${initial}.` : first;
  };

  const twoDigits = (value) => String(value).padStart(2, '0');

  const fetchReview = async (apiBase, language, selection) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    const url = new URL(apiBase);

    url.search = new URLSearchParams({
      productHandle: selection.handle,
      language,
      sort: 'helpful',
      page: '1',
      limit: '100',
    }).toString();

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Review request failed: ${response.status}`);

      const payload = await response.json();
      const reviews = payload?.json?.reviews || payload?.reviews || [];
      const review = reviews.find((item) => Number(item.id) === Number(selection.id));

      if (
        !review ||
        review.ratingOnly ||
        !review.verifiedBuyer ||
        !review.body ||
        Number(review.rating) < 4
      ) {
        return null;
      }

      return {
        ...review,
        product: selection.product,
        productUrl: selection.url || '',
      };
    } finally {
      window.clearTimeout(timer);
    }
  };

  const buildCard = (review, index, total, labels) => {
    const card = document.createElement('article');
    const meta = document.createElement('header');
    const stars = document.createElement('p');
    const quote = document.createElement('blockquote');
    const byline = document.createElement('footer');
    const person = document.createElement('div');
    const name = document.createElement('strong');
    const verified = document.createElement('span');
    const product = document.createElement(review.productUrl ? 'a' : 'span');
    const productText = document.createElement('span');
    const productArrow = document.createElement('span');
    const rating = Math.max(1, Math.min(5, Math.round(Number(review.rating) || 0)));

    card.className = 'diggn-reviews__card';
    card.dataset.reviewCard = String(index);
    card.setAttribute('role', 'group');
    card.setAttribute('aria-roledescription', 'slide');
    card.setAttribute('aria-label', `${index + 1} / ${total}`);

    meta.className = 'diggn-reviews__meta';
    stars.className = 'diggn-reviews__stars';
    stars.textContent = '★'.repeat(rating);
    stars.setAttribute('aria-label', `${rating} ${labels.starsSuffix}`);

    product.className = 'diggn-reviews__product';
    productText.textContent = review.product;
    productArrow.className = 'diggn-reviews__product-arrow';
    productArrow.textContent = labels.productArrow;
    productArrow.setAttribute('aria-hidden', 'true');
    product.append(productText, productArrow);

    if (review.productUrl) {
      product.href = review.productUrl;
      product.setAttribute('aria-label', `${labels.productAction}: ${review.product}`);
    }

    quote.className = 'diggn-reviews__quote';
    quote.textContent = cleanDisplayText(review.body);

    byline.className = 'diggn-reviews__byline';
    person.className = 'diggn-reviews__person';
    name.textContent = formatName(review.authorName);
    verified.className = 'diggn-reviews__verified';
    verified.textContent = labels.verified;
    name.setAttribute('dir', 'auto');

    meta.append(stars, product);
    person.append(name, verified);
    byline.append(person);
    card.append(meta, quote, byline);

    return card;
  };

  const initCarousel = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const configNode = root.querySelector('[data-review-selection]');
    const track = root.querySelector('[data-review-track]');
    const skeletons = root.querySelector('[data-review-skeletons]');
    const autoplayToggle = root.querySelector('[data-review-autoplay]');
    const pauseIcon = root.querySelector('[data-review-pause-icon]');
    const playIcon = root.querySelector('[data-review-play-icon]');
    const previous = root.querySelector('[data-review-previous]');
    const next = root.querySelector('[data-review-next]');
    const current = root.querySelector('[data-review-current]');
    const totalNode = root.querySelector('[data-review-total]');
    const progress = root.querySelector('[data-review-progress]');
    const status = root.querySelector('[data-review-status]');
    const apiBase = root.dataset.apiBase;
    const language = root.dataset.language || 'en';
    const autoplayInterval = Math.max(5000, Number(root.dataset.autoplayInterval) || 7000);
    const labels = {
      verified: root.dataset.verifiedLabel || 'Verified buyer',
      starsSuffix: root.dataset.starsSuffix || 'out of 5 stars',
      productAction: root.dataset.productActionLabel || 'View product',
      productArrow: root.dataset.productArrow || '→',
    };

    if (!configNode || !track || !apiBase) {
      root.hidden = true;
      return;
    }

    let selections = [];

    try {
      selections = JSON.parse(configNode.textContent);
    } catch (error) {
      root.hidden = true;
      return;
    }

    Promise.allSettled(
      selections.map((selection) => fetchReview(apiBase, language, selection))
    )
      .then((results) =>
        results
          .map((result) => (result.status === 'fulfilled' ? result.value : null))
          .filter(Boolean)
      )
      .then((reviews) => {
        if (reviews.length < 3) {
          root.hidden = true;
          return;
        }

        const cards = reviews.map((review, index) =>
          buildCard(review, index, reviews.length, labels)
        );

        track.replaceChildren(...cards);
        skeletons?.remove();
        totalNode.textContent = twoDigits(reviews.length);
        root.setAttribute('aria-busy', 'false');
        root.classList.remove('diggn-reviews--pending');
        root.classList.add('is-ready');
        status.textContent = '';

        let activeIndex = 0;
        let frame = null;
        let autoplayTimer = null;
        let scrollSettleTimer = null;
        let programmaticTargetIndex = null;
        let intersectionObserver = null;
        let isVisible = !('IntersectionObserver' in window);
        let stageHovered = false;
        let focusPaused = false;
        let userPaused = false;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        const syncAutoplayToggle = () => {
          if (!autoplayToggle || !pauseIcon || !playIcon) return;

          autoplayToggle.hidden = reducedMotion.matches;
          autoplayToggle.setAttribute('aria-pressed', String(userPaused));
          autoplayToggle.setAttribute(
            'aria-label',
            userPaused ? root.dataset.playLabel : root.dataset.pauseLabel
          );
          pauseIcon.hidden = userPaused;
          playIcon.hidden = !userPaused;
        };

        const scheduleAutoplay = () => {
          window.clearTimeout(autoplayTimer);
          autoplayTimer = null;

          if (
            userPaused ||
            reducedMotion.matches ||
            !isVisible ||
            stageHovered ||
            focusPaused ||
            document.hidden
          ) {
            return;
          }

          autoplayTimer = window.setTimeout(() => {
            moveTo(activeIndex + 1, true);
            scheduleAutoplay();
          }, autoplayInterval);
        };

        const pauseForManualChoice = () => {
          userPaused = true;
          syncAutoplayToggle();
          scheduleAutoplay();
        };

        const setActive = (index) => {
          activeIndex = Math.max(0, Math.min(cards.length - 1, index));

          cards.forEach((card, cardIndex) => {
            card.classList.toggle('is-active', cardIndex === activeIndex);
            card.setAttribute('aria-current', cardIndex === activeIndex ? 'true' : 'false');
          });

          current.textContent = twoDigits(activeIndex + 1);
          previous.disabled = activeIndex === 0;
          next.disabled = activeIndex === cards.length - 1;
          progress.style.setProperty(
            '--diggn-review-progress',
            String((activeIndex + 1) / cards.length)
          );
        };

        const closestCardIndex = () => {
          const trackRect = track.getBoundingClientRect();
          const center = trackRect.left + trackRect.width / 2;
          let closest = 0;
          let distance = Number.POSITIVE_INFINITY;

          cards.forEach((card, index) => {
            const rect = card.getBoundingClientRect();
            const cardDistance = Math.abs(rect.left + rect.width / 2 - center);

            if (cardDistance < distance) {
              closest = index;
              distance = cardDistance;
            }
          });

          return closest;
        };

        const releaseProgrammaticScroll = () => {
          window.clearTimeout(scrollSettleTimer);
          scrollSettleTimer = null;
          programmaticTargetIndex = null;
        };

        const moveTo = (index, wrap = false) => {
          const targetIndex = wrap
            ? (index + cards.length) % cards.length
            : Math.max(0, Math.min(cards.length - 1, index));
          const target = cards[targetIndex];
          if (!target) return;

          window.clearTimeout(scrollSettleTimer);
          programmaticTargetIndex = targetIndex;

          target.scrollIntoView({
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
          setActive(targetIndex);
          scrollSettleTimer = window.setTimeout(() => {
            releaseProgrammaticScroll();
            setActive(closestCardIndex());
          }, reducedMotion.matches ? 0 : 800);
        };

        track.addEventListener(
          'scroll',
          () => {
            if (programmaticTargetIndex !== null) return;
            if (frame) window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => setActive(closestCardIndex()));
          },
          { passive: true }
        );

        autoplayToggle?.addEventListener('click', () => {
          if (reducedMotion.matches) return;

          userPaused = !userPaused;
          syncAutoplayToggle();
          scheduleAutoplay();
        });

        previous.addEventListener('click', () => {
          pauseForManualChoice();
          moveTo(activeIndex - 1);
        });

        next.addEventListener('click', () => {
          pauseForManualChoice();
          moveTo(activeIndex + 1);
        });

        track.addEventListener('keydown', (event) => {
          if (event.target !== track) return;

          const isRtl = root.dir === 'rtl';

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            pauseForManualChoice();
            moveTo(activeIndex + (isRtl ? -1 : 1));
          }

          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            pauseForManualChoice();
            moveTo(activeIndex + (isRtl ? 1 : -1));
          }
        });

        track.addEventListener('pointerdown', (event) => {
          if (event.isPrimary === false) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          releaseProgrammaticScroll();
          pauseForManualChoice();
        });

        track.addEventListener(
          'wheel',
          (event) => {
            if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) && !event.shiftKey) return;
            releaseProgrammaticScroll();
            pauseForManualChoice();
          },
          { passive: true }
        );

        root.querySelector('.diggn-reviews__stage')?.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          stageHovered = true;
          scheduleAutoplay();
        });

        root.querySelector('.diggn-reviews__stage')?.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          stageHovered = false;
          scheduleAutoplay();
        });

        root.addEventListener('focusin', (event) => {
          if (autoplayToggle?.contains(event.target)) return;
          focusPaused = true;
          scheduleAutoplay();
        });

        root.addEventListener('focusout', (event) => {
          const nextFocus = event.relatedTarget;
          focusPaused = Boolean(
            nextFocus && root.contains(nextFocus) && !autoplayToggle?.contains(nextFocus)
          );
          scheduleAutoplay();
        });

        const handleVisibilityChange = () => scheduleAutoplay();
        const handleReducedMotionChange = () => {
          syncAutoplayToggle();
          scheduleAutoplay();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        reducedMotion.addEventListener?.('change', handleReducedMotionChange);

        if ('IntersectionObserver' in window) {
          intersectionObserver = new IntersectionObserver(
            (entries) => {
              isVisible = Boolean(entries[0]?.isIntersecting);
              scheduleAutoplay();
            },
            { threshold: 0.35 }
          );
          intersectionObserver.observe(root);
        }

        root._diggnReviewCarouselDestroy = () => {
          window.clearTimeout(autoplayTimer);
          window.clearTimeout(scrollSettleTimer);
          if (frame) window.cancelAnimationFrame(frame);
          intersectionObserver?.disconnect();
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          reducedMotion.removeEventListener?.('change', handleReducedMotionChange);
          delete root._diggnReviewCarouselDestroy;
        };

        setActive(0);
        syncAutoplayToggle();
        scheduleAutoplay();
      })
      .catch(() => {
        root.hidden = true;
      });
  };

  roots.forEach(initCarousel);

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.('[data-diggn-review-carousel]');
    if (root) initCarousel(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.('[data-diggn-review-carousel]');
    root?._diggnReviewCarouselDestroy?.();
  });
})();
